import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ArrowBack, Facebook, CloudUpload, Delete, PhotoLibrary, Add } from '@mui/icons-material';
import AdHierarchySidebar from '../../components/AdHierarchySidebar';
import CreativePreviewDialog from '../../components/CreativePreviewDialog';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adsService, Ad } from '../../services/ads.service';
import { errorMessage } from '../../utils/errorMessage';
import { adsetsService } from '../../services/adsets.service';
import { pagesService, Page } from '../../services/pages.service';
import { useUiStore } from '../../stores/ui.store';
import ImageCropper from '../../components/ImageCropper';
import CreativeLibraryPicker from '../../components/creatives/CreativeLibraryPicker';


const callToActions = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'SUBSCRIBE', label: 'Subscribe' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'BOOK_NOW', label: 'Book Now' },
  { value: 'APPLY_NOW', label: 'Apply Now' },
  { value: 'GET_OFFER', label: 'Get Offer' },
  { value: 'NO_BUTTON', label: 'No Button' },
];

interface CreateAdFormData {
  ad_set_id: string;
  page_id: string;
  name: string;
  status: string;
  headline: string;
  body: string;
  call_to_action: string;
  link_url: string;
  image_path: string;
  video_path: string;
  creative_image_url: string;
}

// Facebook recommended aspect ratios
const FB_ASPECT_RATIOS = {
  FEED: 1, // 1:1
  STORIES: 9 / 16, // 9:16
};

// Check if image matches Facebook recommended dimensions
function checkImageDimensions(width: number, height: number): { needsCrop: boolean; suggestedRatio: '1:1' | '9:16' | null } {
  const aspectRatio = width / height;
  const tolerance = 0.05;

  // Check if matches 1:1
  if (Math.abs(aspectRatio - FB_ASPECT_RATIOS.FEED) < tolerance) {
    return { needsCrop: false, suggestedRatio: '1:1' };
  }

  // Check if matches 9:16
  if (Math.abs(aspectRatio - FB_ASPECT_RATIOS.STORIES) < tolerance) {
    return { needsCrop: false, suggestedRatio: '9:16' };
  }

  // Suggest ratio based on which is closer
  const feedDiff = Math.abs(aspectRatio - FB_ASPECT_RATIOS.FEED);
  const storiesDiff = Math.abs(aspectRatio - FB_ASPECT_RATIOS.STORIES);

  return {
    needsCrop: true,
    suggestedRatio: feedDiff < storiesDiff ? '1:1' : '9:16',
  };
}

export default function CreateAdPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [needAdSet, setNeedAdSet] = useState(false);
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams<{ id: string }>();
  const isEditMode = !!editId;
  const queryClient = useQueryClient();
  const { selectedAdAccountId } = useUiStore();
  // Media upload state
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const preselectedAdSetId = searchParams.get('adset_id') || '';

  // Multi-text state
  const [headlines, setHeadlines] = useState<string[]>(['']);
  const [bodies, setBodies] = useState<string[]>(['']);

  // Fetch existing ad when editing
  const { data: existingAd, isFetching: isFetchingAd } = useQuery({
    queryKey: ['ad', editId],
    queryFn: () => adsService.getById(editId!),
    enabled: isEditMode,
  });

  const [initialized, setInitialized] = useState<string | false>(false);
  useEffect(() => {
    if (!existingAd || initialized === editId) return;
    setValue('ad_set_id', existingAd.ad_set_id);
    setValue('page_id', existingAd.page_id || '');
    setValue('name', existingAd.name);
    setValue('status', existingAd.status || 'PAUSED');
    setValue('call_to_action', existingAd.call_to_action || 'LEARN_MORE');
    setValue('link_url', existingAd.link_url || '');
    if (existingAd.image_path) setValue('image_path', existingAd.image_path);
    if (existingAd.video_path) setValue('video_path', existingAd.video_path);

    // Show existing creative preview
    if (existingAd.creative_image_url) {
      setUploadedImageUrl(existingAd.creative_image_url);
    } else if (existingAd.fb_video_id || existingAd.fb_image_hash) {
      // Load preview from FB via media endpoint
      adsService.getMedia(editId!).then((media) => {
        if (media.video_source) {
          setVideoPreview(media.video_source);
        } else if (media.thumbnail_url || media.image_url) {
          setUploadedImageUrl(media.image_url || media.thumbnail_url);
        }
      }).catch(() => {});
    }

    // Multi-text
    if (existingAd.headlines?.length) {
      setHeadlines(existingAd.headlines);
    } else if (existingAd.headline) {
      setHeadlines([existingAd.headline]);
    }
    if (existingAd.bodies?.length) {
      setBodies(existingAd.bodies);
    } else if (existingAd.body) {
      setBodies([existingAd.body]);
    }

    setInitialized(editId || false);
  }, [existingAd]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch ad sets for selection
  const { data: adSetsResult, isLoading: isLoadingAdSets } = useQuery({
    queryKey: ['adsets'],
    queryFn: () => adsetsService.query({ limit: 100 }),
  });

  const adSets = adSetsResult?.data ?? [];

  // Fetch the preselected ad set directly (for sidebar campaign_id before full list loads)
  const { data: preselectedAdSet } = useQuery({
    queryKey: ['adset', preselectedAdSetId],
    queryFn: () => adsetsService.getById(preselectedAdSetId),
    enabled: !!preselectedAdSetId && !adSets.find(a => a.id === preselectedAdSetId),
  });

  // Fetch pages for selection (pages belong to tenant, not ad account)
  const { data: pagesResult, isLoading: isLoadingPages } = useQuery({
    queryKey: ['pages'],
    queryFn: () => pagesService.query({}),
  });

  const pages: Page[] = pagesResult?.data ?? [];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateAdFormData>({
    defaultValues: {
      ad_set_id: preselectedAdSetId,
      page_id: '',
      name: '',
      status: 'PAUSED',
      headline: '',
      body: '',
      call_to_action: 'LEARN_MORE',
      link_url: '',
      image_path: '',
      video_path: '',
      creative_image_url: '',
    },
  });

  // Process a selected file (shared by click and drag-drop)
  const processFileRef = useRef<(file: File) => void>(() => {});
  processFileRef.current = (file: File) => {
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const videoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];

    if (videoTypes.includes(file.type)) {
      handleVideoUpload(file);
      return;
    }

    if (!imageTypes.includes(file.type)) {
      enqueueSnackbar('Choose an image (JPEG, PNG, GIF, WebP) or a video (MP4, WebM).', { variant: 'warning' });
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const { needsCrop } = checkImageDimensions(img.width, img.height);
      setSelectedImage(imageUrl);
      if (needsCrop) {
        setShowCropper(true);
      } else {
        handleDirectUpload(file);
      }
    };
    img.src = imageUrl;
  };
  const processFile = useCallback((file: File) => processFileRef.current(file), []);

  // Handle file selection via input
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    processFile(file);

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFile]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  }, [processFile]);

  // The server files media under this ad set's workspace, so it must be chosen first.
  const uploadAdSetId = (): string | null => {
    const id = existingAd?.ad_set_id || currentAdSetId;
    if (!id) setNeedAdSet(true);
    return id || null;
  };

  // Video upload via signed URL (for large files)
  const handleVideoUpload = async (file: File) => {
    const adSetId = uploadAdSetId();
    if (!adSetId) return;
    setIsUploading(true);
    setUploadProgress(0);
    try {
      // 1. Get signed upload URL from server
      const { uploadUrl, path } = await adsService.getUploadUrl(file.name, file.type, adSetId);

      // 2. Upload directly to GCS with progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // 3. Store the GCS path and show preview
      setValue('video_path', path);
      setVideoPreview(URL.createObjectURL(file));
      // Clear any image
      setValue('image_path', '');
      setUploadedImageUrl(null);
      setSelectedImage(null);
    } catch (error) {
      console.error('Failed to upload video:', error);
      enqueueSnackbar('The video upload failed. Please try again.', { variant: 'error' });
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  // Direct upload without cropping
  const handleDirectUpload = async (file: File) => {
    const adSetId = uploadAdSetId();
    if (!adSetId) return;
    setIsUploading(true);
    try {
      const result = await adsService.uploadImage(file, adSetId);
      setValue('image_path', result.path);
      // Without the reverse of the video handler, the creative keeps the old video.
      setValue('video_path', '');
      setUploadedImageUrl(selectedImage);
      setSelectedImage(null);
    } catch (error) {
      console.error('Failed to upload image:', error);
      enqueueSnackbar('The image upload failed. Please try again.', { variant: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle crop completion
  const handleCropComplete = async (croppedBlob: Blob) => {
    const adSetId = uploadAdSetId();
    if (!adSetId) return;
    setIsUploading(true);
    try {
      const file = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
      const result = await adsService.uploadImage(file, adSetId);
      setValue('image_path', result.path);
      setValue('video_path', '');
      setUploadedImageUrl(URL.createObjectURL(croppedBlob));
      setShowCropper(false);
      setSelectedImage(null);
    } catch (error) {
      console.error('Failed to upload cropped image:', error);
      enqueueSnackbar('The image upload failed. Please try again.', { variant: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  // Remove uploaded image
  // Pick an existing creative from the library
  const handleLibraryPick = (creative: { gcs_path: string; signed_url?: string }) => {
    setValue('image_path', creative.gcs_path);
    setValue('video_path', '');
    setUploadedImageUrl(creative.signed_url || null);
    setSelectedImage(null);
  };

  // Auto-select first page if only one available
  useEffect(() => {
    if (pages.length === 1 && !watch('page_id')) {
      setValue('page_id', pages[0].id);
    }
  }, [pages, setValue, watch]);

  const createMutation = useMutation({
    mutationFn: (payload: Partial<Ad>) =>
      isEditMode ? adsService.update(editId!, payload) : adsService.create(payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      queryClient.invalidateQueries({ queryKey: ['adset-ads'] });
      queryClient.invalidateQueries({ queryKey: ['ad', editId] });
      if (!isEditMode) navigate(`/ads/${result.id}/edit`);
    },
  });

  const formData = watch();
  const selectedPage = pages.find((p) => p.id === formData.page_id);
  const selectedAdSet = adSets.find((a) => a.id === formData.ad_set_id);

  const onSubmit = (data: CreateAdFormData) => {
    const filteredHeadlines = headlines.filter(h => h.trim());
    const filteredBodies = bodies.filter(b => b.trim());

    const payload: Partial<Ad> = {
      ad_set_id: data.ad_set_id,
      page_id: data.page_id,
      name: data.name,
      headline: filteredHeadlines[0] || undefined,
      body: filteredBodies[0] || undefined,
      headlines: filteredHeadlines.length > 1 ? filteredHeadlines : undefined,
      bodies: filteredBodies.length > 1 ? filteredBodies : undefined,
      call_to_action: data.call_to_action || undefined,
      link_url: data.link_url || undefined,
      // null, not undefined: a key JSON drops leaves the old asset in place.
      image_path: data.image_path || null,
      video_path: data.video_path || null,
      creative_image_url: data.creative_image_url || undefined,
    };
    createMutation.mutate(payload);
  };

  const handleSave = () => {
    handleSubmit(onSubmit)();
  };

  const handleCancel = () => {
    if (preselectedAdSetId) {
      navigate(`/adsets/${preselectedAdSetId}`);
    } else {
      navigate('/ads');
    }
  };

  const canSave = () => {
    if (!formData.ad_set_id || !formData.name) return false;
    // In edit mode, page_id may already be set on the server side
    if (!isEditMode && !formData.page_id) return false;
    // Need at least some text
    const hasText = headlines.some(h => h.trim()) || bodies.some(b => b.trim());
    if (!hasText) return false;
    return true;
  };

  // Resolve campaign ID for hierarchy sidebar
  const currentAdSetId = formData.ad_set_id || preselectedAdSetId;
  const selectedAdSetForSidebar = adSets.find(a => a.id === currentAdSetId) || preselectedAdSet;
  const sidebarCampaignId = existingAd?.campaign_id || selectedAdSetForSidebar?.campaign_id;

  return (
    <Box sx={{ display: 'flex', gap: 0, mx: -3, mt: -3, minHeight: 'calc(100vh - 120px)' }}>
      {sidebarCampaignId && (
        <AdHierarchySidebar
          campaignId={sidebarCampaignId}
          adSetId={currentAdSetId || undefined}
          adId={editId}
        />
      )}

      <Box sx={{ flex: 1, p: 3, overflow: 'auto', maxWidth: 900, opacity: (isEditMode && isFetchingAd && initialized !== editId) ? 0.4 : 1, transition: 'opacity 0.15s' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={handleCancel}>
            Back
          </Button>
          <Typography variant="h4" fontWeight={600} sx={{ ml: 2 }}>
            {isEditMode ? 'Edit Ad' : 'Create Ad'}
          </Typography>
        </Box>

        <Card>
        <CardContent>
          {createMutation.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage(createMutation.error, `Failed to ${isEditMode ? 'update' : 'create'} ad. Please try again.`)}
            </Alert>
          )}

          {/* Section: Ad Set & Page */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Ad Set & Page</Typography>
            <Grid container spacing={3}>
              {adSets.length === 0 && !isLoadingAdSets && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">
                    No ad sets found. Please create an ad set first.
                  </Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="ad_set_id"
                  control={control}
                  rules={{ required: 'Ad Set is required' }}
                  render={({ field }) => (
                    <Autocomplete
                      options={adSets}
                      getOptionLabel={(o) => o.name}
                      value={adSets.find(a => a.id === field.value) || preselectedAdSet || (existingAd?.ad_set ? { id: existingAd.ad_set_id, name: existingAd.ad_set.name } as any : null)}
                      onChange={(_, v) => field.onChange(v?.id || '')}
                      disabled={isLoadingAdSets || !!preselectedAdSetId || isEditMode}
                      loading={isLoadingAdSets}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Ad Set"
                          error={!!errors.ad_set_id}
                          helperText={errors.ad_set_id?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                {pages.length === 0 && !isLoadingPages && selectedAdAccountId && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No Facebook Pages found. Go to the{' '}
                    <Box component="span" sx={{ fontWeight: 600, cursor: 'pointer', color: 'primary.main' }} onClick={() => navigate('/pages')}>
                      Pages section
                    </Box>
                    {' '}to sync your pages from Facebook.
                  </Alert>
                )}
                <Controller
                  name="page_id"
                  control={control}
                  rules={{ required: 'Facebook Page is required' }}
                  render={({ field }) => (
                    <Autocomplete
                      options={pages}
                      getOptionLabel={(o) => o.name}
                      value={pages.find(p => p.id === field.value) || null}
                      onChange={(_, v) => field.onChange(v?.id || '')}
                      disabled={isLoadingPages || pages.length === 0}
                      loading={isLoadingPages}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={option.picture_url} sx={{ width: 24, height: 24 }}>
                              <Facebook sx={{ fontSize: 16 }} />
                            </Avatar>
                            {option.name}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Facebook Page"
                          error={!!errors.page_id}
                          helperText={errors.page_id?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Ad name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Ad Name"
                      error={!!errors.name}
                      helperText={errors.name?.message || 'Internal name for this ad'}
                    />
                  )}
                />
              </Grid>

            </Grid>

          {/* Section: Creative */}
          <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>Ad Creative</Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Upload image or video, add headlines and text
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create the content that will be shown in your ad.
                </Typography>
              </Grid>

              {/* Image Upload Section */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Ad Image
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                {!uploadedImageUrl && !videoPreview ? (
                  <Box>
                    <Box
                      onClick={() => !isUploading && fileInputRef.current?.click()}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      sx={{
                        border: '2px dashed',
                        borderColor: isDragOver ? 'primary.main' : 'grey.400',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: isUploading ? 'default' : 'pointer',
                        transition: 'all 0.2s',
                        bgcolor: isDragOver ? 'action.hover' : 'transparent',
                        '&:hover': isUploading ? {} : {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      {isUploading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <CircularProgress
                            variant={uploadProgress !== null ? 'determinate' : 'indeterminate'}
                            value={uploadProgress || 0}
                            size={40}
                          />
                          {uploadProgress !== null && (
                            <Typography variant="body2" color="text.secondary">
                              Uploading... {uploadProgress}%
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <>
                          <CloudUpload sx={{ fontSize: 48, color: isDragOver ? 'primary.main' : 'grey.400', mb: 1 }} />
                          <Typography variant="body1" color={isDragOver ? 'primary.main' : 'text.secondary'}>
                            {isDragOver ? 'Drop file here' : 'Click or drag to upload image or video'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Image: 1:1 (1080x1080) for Feed, 9:16 for Stories. Video: MP4 up to 4GB.
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<PhotoLibrary />}
                        onClick={() => setLibraryPickerOpen(true)}
                        disabled={isUploading}
                      >
                        Pick from creatives library
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'grey.300',
                    }}
                  >
                    {videoPreview ? (
                      <video
                        src={videoPreview}
                        controls
                        style={{ maxWidth: 400, maxHeight: 400, display: 'block', cursor: 'pointer' }}
                      />
                    ) : (
                      <img
                        src={uploadedImageUrl!}
                        onClick={() => isEditMode && editId && setPreviewOpen(true)}
                        alt="Uploaded ad image"
                        style={{ maxWidth: 300, maxHeight: 300, display: 'block', cursor: isEditMode ? 'pointer' : 'default' }}
                      />
                    )}
                    <IconButton
                      onClick={() => {
                        setValue('image_path', '');
                        setValue('video_path', '');
                        setUploadedImageUrl(null);
                        setVideoPreview(null);
                        setSelectedImage(null);
                      }}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Grid>

              {/* Headlines (up to 5) */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Headlines {headlines.length > 1 && `(${headlines.length}/5 — FB tests combinations)`}
                </Typography>
                {headlines.map((h, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Headline ${i + 1}`}
                      value={h}
                      onChange={(e) => {
                        const next = [...headlines];
                        next[i] = e.target.value;
                        setHeadlines(next);
                      }}
                      inputProps={{ maxLength: 100 }}
                      helperText={i === 0 ? 'Up to 40 characters recommended' : undefined}
                    />
                    {headlines.length > 1 && (
                      <IconButton size="small" onClick={() => setHeadlines(headlines.filter((_, j) => j !== i))}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {headlines.length < 5 && (
                  <Button size="small" startIcon={<Add />} onClick={() => setHeadlines([...headlines, ''])}>
                    Add headline
                  </Button>
                )}
              </Grid>

              {/* Primary Texts (up to 5) */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Primary Text {bodies.length > 1 && `(${bodies.length}/5 — FB tests combinations)`}
                </Typography>
                {bodies.map((b, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      label={`Primary Text ${i + 1}`}
                      value={b}
                      onChange={(e) => {
                        const next = [...bodies];
                        next[i] = e.target.value;
                        setBodies(next);
                      }}
                      inputProps={{ maxLength: 500 }}
                      helperText={i === 0 ? 'Up to 125 characters recommended' : undefined}
                    />
                    {bodies.length > 1 && (
                      <IconButton size="small" onClick={() => setBodies(bodies.filter((_, j) => j !== i))}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {bodies.length < 5 && (
                  <Button size="small" startIcon={<Add />} onClick={() => setBodies([...bodies, ''])}>
                    Add primary text
                  </Button>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="call_to_action"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={callToActions}
                      getOptionLabel={(o) => o.label}
                      value={callToActions.find(c => c.value === field.value) || null}
                      onChange={(_, v) => field.onChange(v?.value || '')}
                      renderInput={(params) => <TextField {...params} label="Call to Action" />}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="link_url"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Destination URL"
                      type="url"
                      placeholder="https://example.com/landing-page"
                      helperText="The URL where users will be directed when they click your ad"
                    />
                  )}
                />
              </Grid>
            </Grid>

          {/* Save Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={createMutation.isPending || !canSave()}
            >
              {createMutation.isPending ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Ad')}
            </Button>
          </Box>

          {false as boolean && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Review Your Ad
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Ad Settings
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Ad Set
                  </Typography>
                  <Typography variant="body1">
                    {selectedAdSet?.name || '-'}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Facebook Page
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    {selectedPage && (
                      <>
                        <Avatar src={selectedPage.picture_url} sx={{ width: 24, height: 24 }}>
                          <Facebook sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography variant="body1">{selectedPage.name}</Typography>
                      </>
                    )}
                    {!selectedPage && <Typography variant="body1">-</Typography>}
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Ad Name
                  </Typography>
                  <Typography variant="body1">{formData.name || '-'}</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1">{formData.status}</Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                    Ad Creative
                  </Typography>
                </Grid>

                {uploadedImageUrl && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Ad Image
                    </Typography>
                    <Box
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'inline-block',
                        border: '1px solid',
                        borderColor: 'grey.300',
                      }}
                    >
                      <img
                        src={uploadedImageUrl}
                        alt="Ad preview"
                        style={{ maxWidth: 200, maxHeight: 200, display: 'block' }}
                      />
                    </Box>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    {headlines.filter(h => h.trim()).length > 1 ? `Headlines (${headlines.filter(h => h.trim()).length})` : 'Headline'}
                  </Typography>
                  {headlines.filter(h => h.trim()).map((h, i) => (
                    <Typography key={i} variant="body1">{h}</Typography>
                  ))}
                  {headlines.filter(h => h.trim()).length === 0 && <Typography variant="body1">-</Typography>}
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    {bodies.filter(b => b.trim()).length > 1 ? `Primary Texts (${bodies.filter(b => b.trim()).length})` : 'Primary Text'}
                  </Typography>
                  {bodies.filter(b => b.trim()).map((b, i) => (
                    <Typography key={i} variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 0.5 }}>{b}</Typography>
                  ))}
                  {bodies.filter(b => b.trim()).length === 0 && <Typography variant="body1">-</Typography>}
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Call to Action
                  </Typography>
                  <Typography variant="body1">
                    {callToActions.find((c) => c.value === formData.call_to_action)?.label || '-'}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    Destination URL
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {formData.link_url || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

        </CardContent>
      </Card>

      {/* Image Cropper Dialog */}
      {selectedImage && (
        <ImageCropper
          image={selectedImage}
          open={showCropper}
          onClose={() => {
            setShowCropper(false);
            setSelectedImage(null);
          }}
          onCropComplete={handleCropComplete}
          loading={isUploading}
        />
      )}

      {/* Creative Preview Dialog */}
      {isEditMode && editId && (
        <CreativePreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          adId={editId}
        />
      )}

      {/* Creative Library Picker */}
      <Dialog open={needAdSet} onClose={() => setNeedAdSet(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Choose an ad set first</DialogTitle>
        <DialogContent>
          <Typography>Media is stored with the ad set's workspace, so pick the ad set before uploading.</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNeedAdSet(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      <CreativeLibraryPicker
        open={libraryPickerOpen}
        onClose={() => setLibraryPickerOpen(false)}
        onSelect={handleLibraryPick}
        type="image"
        title="Pick an image from your library"
      />
      </Box>
    </Box>
  );
}
